export type Inline =
	| { kind: "text"; text: string }
	| { kind: "bold"; text: string }
	| { kind: "italic"; text: string }
	| { kind: "link"; text: string; href: string };

export type Block =
	| { kind: "heading"; level: 1 | 2 | 3; content: Inline[] }
	| { kind: "list"; items: Inline[][] }
	| { kind: "paragraph"; lines: Inline[][] };

const INLINE_PATTERN =
	/\[([^\]]+)\]\(([^()\s]+)\)|\*\*([^*]+)\*\*|\*([^*\s](?:[^*]*[^*\s])?)\*/gu;

const HEADING_PATTERN = /^(#{1,3})\s+(.+)$/u;

function isHttp(href: string) {
	return /^https?:\/\//iu.test(href);
}

function headingLevel(hashes: string): 1 | 2 | 3 {
	if (hashes.length === 1) {
		return 1;
	}

	if (hashes.length === 2) {
		return 2;
	}

	return 3;
}

function startsHeading(line: string) {
	return /^#{1,3}\s/u.test(line);
}

function startsItem(line: string) {
	return line.startsWith("- ");
}

function parseInline(text: string): Inline[] {
	const parts: Inline[] = [];

	let cursor = 0;

	INLINE_PATTERN.lastIndex = 0;

	for (let match = INLINE_PATTERN.exec(text); match !== null; match = INLINE_PATTERN.exec(text)) {
		if (match.index > cursor) {
			parts.push({ kind: "text", text: text.slice(cursor, match.index) });
		}

		const groups = match as unknown as (string | undefined)[];
		const whole = match[0];
		const linkText = groups[1];
		const href = groups[2];
		const bold = groups[3];
		const italic = groups[4];

		if (linkText !== undefined && href !== undefined) {
			if (isHttp(href)) {
				parts.push({ kind: "link", text: linkText, href });
			} else {
				parts.push({ kind: "text", text: whole });
			}
		} else if (bold !== undefined) {
			parts.push({ kind: "bold", text: bold });
		} else if (italic !== undefined) {
			parts.push({ kind: "italic", text: italic });
		}

		cursor = match.index + whole.length;
	}

	if (cursor < text.length) {
		parts.push({ kind: "text", text: text.slice(cursor) });
	}

	return parts;
}

export const Markdown = {
	inline(text: string) {
		return parseInline(text);
	},

	blocks(md: string): Block[] {
		const lines = md.split("\n").map((line) => line.trim());

		const blocks: Block[] = [];

		let i = 0;

		while (i < lines.length) {
			const line = lines[i];

			if (line === "") {
				i += 1;
				continue;
			}

			const heading = line.match(HEADING_PATTERN);

			if (heading) {
				blocks.push({
					kind: "heading",
					level: headingLevel(heading[1]),
					content: parseInline(heading[2]),
				});

				i += 1;
				continue;
			}

			if (startsItem(line)) {
				const items: Inline[][] = [];

				while (i < lines.length && startsItem(lines[i])) {
					items.push(parseInline(lines[i].slice(2).trim()));
					i += 1;
				}

				blocks.push({ kind: "list", items });
				continue;
			}

			const paragraph: Inline[][] = [];

			while (
				i < lines.length &&
				lines[i] !== "" &&
				!startsHeading(lines[i]) &&
				!startsItem(lines[i])
			) {
				paragraph.push(parseInline(lines[i]));
				i += 1;
			}

			blocks.push({ kind: "paragraph", lines: paragraph });
		}

		return blocks;
	},
};
